import React, { useEffect } from 'react';

import { track, useMatchMedia } from '@betterment/js-runtime';
import { Box, SectionLayout } from '@betterment/style-closet-react';
import { logCardClick, logCardDismissal, logContentCardImpressions, requestImmediateDataFlush } from '@braze/web-sdk';

import { AnalyticFlows } from './constants';
import { AccountLevelBanner } from './ContentCards/AccountLevelBanner';
import Guide from './ContentCards/Guide';
import LegalBanner from './ContentCards/LegalBanner';
import OverlayNotice from './ContentCards/OverlayNotice';
import RewardsContentCard from './ContentCards/RewardsContentCard';
import SecondaryCard from './ContentCards/SecondaryCard';
import { WithdrawalBanner } from './ContentCards/WithdrawalBanner';
import { MessageProvider, useMessageByLocation } from './context/MessageContext';
import { ContentCardLocation } from './data';
import disclosureInitializer from './disclosureInitializer';
import { MessageCardLoader } from './shared/MessageCardLoader';

import { locationAssign, navigateOut } from '../Shared/path-helpers';

type Props = {
  location: ContentCardLocation;
  hasNoExistingGoals?: boolean;
  goalId?: number;
  isSummaryPage?: boolean;
};

export const MessageCardContent: React.FC<Props> = ({ location, hasNoExistingGoals = false, goalId }) => {
  const {
    filterAndLogMessageByLocation,
    message,
    contentCard: contentCardForBrazeEvent,
    refresh,
    isLoading: isMessageRefreshing,
    isReadyToFilter,
  } = useMessageByLocation();
  const isAtLeastMedium = useMatchMedia('atLeastMedium');
  const bottomSpacing = isAtLeastMedium ? '8' : '3';

  const analyticsFlow = AnalyticFlows[location];

  useEffect(() => {
    if (message) {
      disclosureInitializer.update(location, message.extras?.footerDisclosure);
    }
  }, [location, message]);

  useEffect(() => {
    if (isReadyToFilter) {
      void filterAndLogMessageByLocation({ location, goalId });
    }
  }, [isReadyToFilter, filterAndLogMessageByLocation, goalId, location]);

  useEffect(() => {
    if (contentCardForBrazeEvent) {
      track('ElementViewed', {
        name: contentCardForBrazeEvent.extras?.name,
        module: location,
        flow: analyticsFlow,
      });
      logContentCardImpressions([contentCardForBrazeEvent]);
    }
  }, [contentCardForBrazeEvent, analyticsFlow, location]);

  if (isMessageRefreshing && location === 'accountOverviewGuide') {
    return <MessageCardLoader />;
  }

  if (isMessageRefreshing || !message || !contentCardForBrazeEvent) return null;

  const handleCardClick = (event: React.MouseEvent) => {
    event.preventDefault();
    logCardClick(contentCardForBrazeEvent, true);
    requestImmediateDataFlush(() => {
      if (message.url && message.url !== '#') {
        if (message.extras?.linkTarget === '_blank') {
          navigateOut(message.url);
        } else {
          locationAssign(message.url);
        }
      } else {
        refresh();
      }
    });
  };

  const handleDismissal = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    track('MessageDismissed', {
      name: contentCardForBrazeEvent.extras?.name,
      module: location,
      flow: analyticsFlow,
    });
    logCardDismissal(contentCardForBrazeEvent);
    refresh(contentCardForBrazeEvent?.id);
  };

  switch (location) {
    case 'rewardsCard':
      return (
        <SectionLayout mb={9}>
          <SectionLayout.ContentWrapper>
            <RewardsContentCard
              {...message}
              handleCardClick={handleCardClick}
              handleDismissal={handleDismissal}
              trackingModule={location}
              trackingFlow={analyticsFlow}
            />
          </SectionLayout.ContentWrapper>
        </SectionLayout>
      );
    case 'summaryGuide':
      return (
        <Guide
          {...message}
          handleCardClick={handleCardClick}
          handleDismissal={handleDismissal}
          trackingModule={location}
          trackingFlow={analyticsFlow}
        />
      );
    case 'accountOverviewGuide':
      return (
        <Guide
          {...message}
          handleCardClick={handleCardClick}
          handleDismissal={handleDismissal}
          trackingModule={location}
          trackingFlow={analyticsFlow}
        />
      );
    case 'summarySecondaryCard':
      return (
        <SecondaryCard
          {...message}
          handleCardClick={handleCardClick}
          handleDismissal={handleDismissal}
          trackingModule={location}
          trackingFlow={analyticsFlow}
        />
      );
    case 'accountOverviewSecondaryCard':
      return (
        <SecondaryCard
          {...message}
          handleCardClick={handleCardClick}
          handleDismissal={handleDismissal}
          trackingModule={location}
          trackingFlow={analyticsFlow}
          isAccountOverview={true}
        />
      );
    case 'envelopeAccountPageGuide':
      return (
        <Guide
          {...message}
          handleCardClick={handleCardClick}
          handleDismissal={handleDismissal}
          trackingModule={location}
          trackingFlow={analyticsFlow}
        />
      );
    case 'envelopeAccountPageSecondaryCard':
      return (
        <SecondaryCard
          {...message}
          handleCardClick={handleCardClick}
          handleDismissal={handleDismissal}
          trackingModule={location}
          trackingFlow={analyticsFlow}
          isAccountOverview={true}
        />
      );
    case 'summaryLegalBanner':
      return (
        <Box mt={['-3.2rem', '-2.2rem', '-0.1rem']} pb="8" mb={hasNoExistingGoals ? bottomSpacing : '0'}>
          <LegalBanner
            {...message}
            handleCardClick={handleCardClick}
            handleDismissal={handleDismissal}
            trackingModule={location}
            trackingFlow={analyticsFlow}
          />
        </Box>
      );
    case 'summaryAccountLevelBanner':
      return (
        <AccountLevelBanner
          {...message}
          handleCardClick={handleCardClick}
          handleDismissal={handleDismissal}
          trackingModule={location}
          trackingFlow={analyticsFlow}
        />
      );
    case 'summaryOverlayNotice':
      return message.extras.interruptiveMessage?.toLowerCase() === 'yes' ? (
        <OverlayNotice
          {...message}
          handleCardClick={handleCardClick}
          handleDismissal={handleDismissal}
          trackingModule={location}
          trackingFlow={analyticsFlow}
        />
      ) : null;
    case 'accountWithdrawal401k':
      return (
        <WithdrawalBanner
          {...message}
          handleCardClick={handleCardClick}
          trackingModule={location}
          trackingFlow={analyticsFlow}
        />
      );
    default:
      return null;
  }
};

const MessageCard: React.FC<Props> = props => {
  return (
    <MessageProvider>
      <MessageCardContent {...props} />
    </MessageProvider>
  );
};

export default MessageCard;
